import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
// The published page's own stylesheet (Tailwind with full preflight, the page
// rules, the canvas keyframes and the inline-editing styles), compiled by Vite
// as a string. It only ever goes into preview frames: the staff document never
// loads it.
import landingCss from '../landing.css?inline';

/** Geist and Geist Mono, the faces published pages fall back to (the same
 *  link web/landing-public.html carries). */
export const PAGE_FONT_HREF = 'https://fonts.googleapis.com/css2?family=Geist:wght@400..700&family=Geist+Mono:wght@400..600&display=swap';

/** Fills a preview frame's head: the viewport meta first (see setViewport),
 *  then the page stylesheet and the font links. Nothing from the staff
 *  document is copied in. */
export function fillFrameHead(doc: Document, css: string = landingCss) {
  const style = doc.createElement('style');
  style.setAttribute('data-page-css', '');
  style.textContent = css;
  doc.head.appendChild(style);
  for (const [href, cross] of [['https://fonts.googleapis.com', false], ['https://fonts.gstatic.com', true]] as const) {
    const link = doc.createElement('link');
    link.rel = 'preconnect';
    link.href = href;
    if (cross) link.crossOrigin = '';
    doc.head.appendChild(link);
  }
  const font = doc.createElement('link');
  font.rel = 'stylesheet';
  font.href = PAGE_FONT_HREF;
  doc.head.appendChild(font);
}

/** A field the visitor (or the editor) is typing into. Checked by tag name and
 *  attribute rather than instanceof: the target lives in the frame's own realm. */
function isFieldTarget(target: EventTarget | null): boolean {
  const el = target as (Element & { isContentEditable?: boolean }) | null;
  if (!el || typeof el.tagName !== 'string') return false;
  const tag = el.tagName.toLowerCase();
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
  if (el.isContentEditable) return true;
  // jsdom has no isContentEditable; the attribute covers it there.
  return typeof el.closest === 'function' && !!el.closest('[contenteditable]:not([contenteditable="false"])');
}

type KeyLike = Pick<KeyboardEvent, 'key' | 'metaKey' | 'ctrlKey' | 'shiftKey' | 'altKey' | 'target' | 'defaultPrevented' | 'isComposing'>;

/** The keys the editor shell listens for on its own window: Escape (deselect,
 *  close a sheet), undo / redo (Cmd/Ctrl+Z, Cmd/Ctrl+Shift+Z, Cmd/Ctrl+Y) and
 *  save (Cmd/Ctrl+S, not with Shift). A click inside the frame moves keyboard
 *  focus into it, so without forwarding these keydowns never reach the parent.
 *  A field being typed in keeps its keys: its own Escape (the inline editor
 *  reverts) and native text undo win, and the editor catches Cmd/Ctrl+S from
 *  its own fields through React. Pure. */
export function shouldForwardKey(e: KeyLike): boolean {
  if (e.defaultPrevented || e.isComposing) return false;
  if (isFieldTarget(e.target)) return false;
  if (e.key === 'Escape') return true;
  if (!(e.metaKey || e.ctrlKey) || e.altKey) return false;
  const k = e.key.toLowerCase();
  return k === 'z' || ((k === 'y' || k === 's') && !e.shiftKey);
}

/** data-editor-mode on the frame's <html>, or none. */
function markMode(doc: Document, mode: string | undefined) {
  const html = doc.documentElement;
  if (!html) return;
  if (mode) html.setAttribute('data-editor-mode', mode);
  else html.removeAttribute('data-editor-mode');
}

/** Re-dispatch a keydown from the frame on the parent document (same key and
 *  modifiers). It bubbles from there to the parent window, where the editor's
 *  listeners are; a listener that handles it (preventDefault) cancels the
 *  original too. Dispatching on the document, not on the iframe element, keeps
 *  React's own handlers from seeing the key twice. */
function forwardKey(e: KeyboardEvent, host: Document) {
  const view = host.defaultView;
  if (!view) return;
  const copy = new view.KeyboardEvent('keydown', {
    key: e.key, code: e.code, location: e.location, repeat: e.repeat,
    ctrlKey: e.ctrlKey, metaKey: e.metaKey, shiftKey: e.shiftKey, altKey: e.altKey,
    bubbles: true, cancelable: true,
  });
  host.dispatchEvent(copy);
  if (copy.defaultPrevented) e.preventDefault();
}

/** Renders its children inside a same-origin iframe so the page's responsive
 *  breakpoints (sm:/md:/lg:) evaluate against the IFRAME's width (a real
 *  mobile viewport) instead of the desktop window. Without this, a narrow
 *  preview box still triggers desktop breakpoints and the preview doesn't match
 *  an actual phone. The page stylesheet (landing.css) and the Geist font links
 *  are injected, so the frame looks exactly like a published page, and the
 *  staff app's styles (and its dark mode) never leak in.
 *
 *  Critically, a `<meta name="viewport">` pins the layout viewport to the
 *  iframe's EXACT measured pixel width. Without it, mobile browsers (Safari /
 *  Chrome on a phone) give a src-less iframe a ~980px default layout viewport,
 *  so `md:` fires and the side hero / image+text split render two-up and
 *  squished even inside a ~388px frame. An explicit `width=<px>` (not
 *  `device-width`, which can resolve to a tablet's >=768px screen) is honored by
 *  every browser, so breakpoints always match the frame. A ResizeObserver keeps
 *  it in sync on rotation / window resize, and the whole thing re-runs on the
 *  iframe `load` event in case the contentDocument was the transient blank doc
 *  at mount.
 *
 *  The width is the frame's LAYOUT width (offsetWidth), not its size on screen:
 *  the editor canvas lays a desktop page out at 1024px or more and scales the
 *  whole frame down, and the page must still see its real width.
 *
 *  Escape, undo / redo and save pressed inside the frame are forwarded to the
 *  parent (see shouldForwardKey), so the editor's shortcuts keep working after
 *  a click on the canvas. */
export default function IframePreview({ children, className, style, title = 'Mobile preview', editorMode }: {
  children: React.ReactNode; className?: string; style?: React.CSSProperties;
  /** The frame's accessible name. */
  title?: string;
  /** The editor canvas mode ('edit' | 'try'), set as data-editor-mode on the
   *  frame's <html> so rules inside the frame can be scoped to it. Absent: no
   *  attribute (every other preview). */
  editorMode?: string;
}) {
  const ref = useRef<HTMLIFrameElement>(null);
  const [mount, setMount] = useState<HTMLElement | null>(null);
  const modeRef = useRef(editorMode);
  modeRef.current = editorMode;

  useEffect(() => {
    const iframe = ref.current;
    if (!iframe) return;

    // The layout width: a transform (the canvas scale) never changes it.
    const frameWidth = () => {
      const w = Math.round(iframe.offsetWidth);
      return w > 0 ? w : 390;
    };

    const setViewport = (doc: Document) => {
      let meta = doc.querySelector('meta[name="viewport"]') as HTMLMetaElement | null;
      if (!meta) {
        meta = doc.createElement('meta');
        meta.name = 'viewport';
        // Keep it first so it applies before the stylesheets parse.
        doc.head.insertBefore(meta, doc.head.firstChild);
      }
      meta.content = `width=${frameWidth()}, initial-scale=1, viewport-fit=cover`;
    };

    // Keyboard forwarding follows the frame's current document (the `load`
    // re-sync below can hand us a new one).
    let keyDoc: Document | null = null;
    const onKey = (e: KeyboardEvent) => { if (shouldForwardKey(e)) forwardKey(e, iframe.ownerDocument); };
    const listenKeys = (doc: Document) => {
      if (keyDoc === doc) return;
      keyDoc?.removeEventListener('keydown', onKey);
      doc.addEventListener('keydown', onKey);
      keyDoc = doc;
    };

    const sync = () => {
      const doc = iframe.contentDocument;
      if (!doc) return;
      setViewport(doc); // pin the layout viewport to the frame's pixel width FIRST
      // The initial about:blank load can fire after the first sync. Reuse the
      // same document's stylesheet/font nodes instead of reparsing all CSS and
      // tearing out font links that the rendered page added in the meantime.
      if (!doc.head.querySelector('style[data-page-css]')) fillFrameHead(doc);
      doc.documentElement.style.colorScheme = 'normal';
      doc.body.style.margin = '0';
      markMode(doc, modeRef.current);
      listenKeys(doc);
      setMount(doc.body);
    };

    sync();
    iframe.addEventListener('load', sync);
    // Keep the viewport width correct if the frame resizes (rotation, window).
    const ro = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => { const doc = iframe.contentDocument; if (doc) setViewport(doc); })
      : null;
    ro?.observe(iframe);
    return () => {
      iframe.removeEventListener('load', sync);
      ro?.disconnect();
      keyDoc?.removeEventListener('keydown', onKey);
    };
  }, []);

  // Edit ↔ Try it flips the attribute in place (no reload, scroll kept).
  useEffect(() => {
    const doc = ref.current?.contentDocument;
    if (doc) markMode(doc, editorMode);
  }, [editorMode]);

  return (
    <iframe ref={ref} title={title} className={className} style={{ border: 0, display: 'block', ...style }}>
      {mount && createPortal(children, mount)}
    </iframe>
  );
}
